"use client";

import { useEffect, useRef } from "react";

/** Inbox / room revision polls. */
export const LIVE_POLL_MS = 4_000;

type Options = {
  /** Called when the server fingerprint moves — prefer a targeted reload over global refresh. */
  onChange: () => void;
};

/**
 * Polls a cheap revision endpoint and invokes `onChange` when it moves.
 * Does not call global `refresh()` — that was remounting the room and
 * restarting every poller into a request storm.
 */
export function usePollRefresh(
  revision: string,
  fetchRevision: () => Promise<string | null>,
  { onChange }: Options,
) {
  const revisionRef = useRef(revision);
  const onChangeRef = useRef(onChange);
  const fetchRef = useRef(fetchRevision);
  const inFlightRef = useRef(false);
  const lastSeenServerRef = useRef<string | null>(null);

  revisionRef.current = revision;
  onChangeRef.current = onChange;
  fetchRef.current = fetchRevision;

  useEffect(() => {
    let cancelled = false;

    const tick = async () => {
      if (cancelled || document.visibilityState !== "visible" || inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        const next = await fetchRef.current();
        if (
          !cancelled &&
          next &&
          next !== revisionRef.current &&
          next !== lastSeenServerRef.current
        ) {
          lastSeenServerRef.current = next;
          onChangeRef.current();
        }
      } catch {
        // retry next interval
      } finally {
        inFlightRef.current = false;
      }
    };

    // No immediate tick on mount — that re-fired every time the room remounted.
    const id = window.setInterval(() => void tick(), LIVE_POLL_MS);
    const onVisibility = () => {
      if (document.visibilityState === "visible") void tick();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);
}
